import { Map } from "immutable";

import { Action } from "common/types/redux";

import AssigneeAggregatedStats from "seneca-common/features/assignments/features/teacher-assignments/state/models/Assignee/AggregatedStats";
import {
  specsMap,
  UnsupportedSpecTypeError
} from "seneca-common/features/assignments/state";
import { specTypes } from "seneca-common/features/assignments/state/models/spec/consts";
import makeAssignmentReducer, {
  MakeAssignment
} from "seneca-common/features/assignments/utils/reducers/makeAssignmentReducer";
import { formatDateTimeString } from "seneca-common/utils/functions/time";

import actionTypes from "../../../../action-types";
import Assignee from "../../../../models/Assignee";
import AssigneeQuestionStat from "../../../../models/Assignee/QuestionStat";
import AssigneeSectionStat from "../../../../models/Assignee/SectionStat";
import QuestionsAggregation, {
  QuestionAggregation
} from "../../../../models/QuestionsAggregation";
import SectionAggregation from "../../../../models/SectionAggregation";
import TeacherAssignment from "../../../../models/TeacherAssignment";

import type {
  Assignee as AssigneeReceived,
  SectionAggregation as SectionStatReceived,
  QuestionAggregations as QuestionsStatReceived,
  UserQuestionStats as UserQuestionStatsReceived
} from "../../../../models/TeacherAssignmentReceived";

export default function teacherAssignmentReducer(
  assignment: TeacherAssignment = new TeacherAssignment(),
  action: Action<any>
) {
  return baseAssignmentReducer(assignment, action);
}

const baseAssignmentReducer = makeAssignmentReducer({
  makeAssignment,
  receiveAssignment: receiveTeacherAssignment,
  receiveActionType: actionTypes.RECEIVE_ASSIGNMENT
});

function receiveTeacherAssignment<
  A extends TeacherAssignment,
  R extends Object
>(state: A, received: R, makeAssignment: MakeAssignment<A>): A {
  const { id, specType, spec } = received as any;

  if (!(specTypes as any)[specType]) {
    throw new UnsupportedSpecTypeError(id, specType);
  }

  const newState = makeAssignment({
    id,
    ...received,
    spec: (specsMap as any)[specType].fromJS(spec)
  });

  return state.mergeWith((oldVal, newVal) => newVal ?? oldVal, newState);
}

function makeAssignment(values: any) {
  return new TeacherAssignment({
    ...values,
    startDate: formatDateTimeString(values.startDate),
    dueDate: formatDateTimeString(values.dueDate),
    assignmentCutoffDate: formatDateTimeString(values.assignmentCutoffDate),
    assignees: assembleAssignees(values.assignees),
    sectionAggregations: assembleSectionAggregations(
      values.sectionAggregations
    ),
    questionAggregations: assembleQuestionAggregations(
      values.questionAggregations
    )
  });
}

function assembleSectionAggregations(
  sectionAggregations?: SectionStatReceived[]
) {
  if (!sectionAggregations) {
    return null;
  }
  if (Array.isArray(sectionAggregations)) {
    return Map(
      sectionAggregations.reduce((acc, { sectionId, ...rest }) => {
        return {
          ...acc,
          [sectionId]: new SectionAggregation(rest)
        };
      }, {})
    );
  }
  return Map(
    Object.keys(sectionAggregations).reduce((acc: any, sectionId: string) => {
      acc[sectionId] = new SectionAggregation(sectionAggregations[sectionId]);
      return acc;
    }, {})
  );
}

function assembleQuestionAggregations(
  questionAggregations?: QuestionsStatReceived
) {
  if (!questionAggregations) {
    return null;
  }

  return new QuestionsAggregation({
    averageMark: questionAggregations.averageMark,
    marksTotal: questionAggregations.marksTotal,
    questions: Map(
      questionAggregations.questions.reduce(
        (acc, { questionId, ...rest }) => ({
          ...acc,
          [questionId]: new QuestionAggregation(rest)
        }),
        {}
      )
    )
  });
}

function assembleAssignees(assignees?: AssigneeReceived[]) {
  if (!assignees) {
    return null;
  }

  return Map<string, Assignee>(
    assignees.reduce((acc, { userId, ...rest }) => {
      return {
        ...acc,
        [userId]: new Assignee({
          ...rest,
          aggregatedStats: new AssigneeAggregatedStats(rest.aggregatedStats),
          status: rest.status && rest.status.toLowerCase(),
          sectionStats: assembleUserSectionStats(rest.sectionStats),
          questionStats: assembleUserQuestionStats(rest.questionStats)
        })
      };
    }, {})
  );
}

function assembleUserSectionStats(
  sectionStats?: AssigneeReceived["sectionStats"]
): Assignee["sectionStats"] {
  if (!sectionStats) {
    return Map() as Assignee["sectionStats"];
  }

  return Map(
    Object.keys(sectionStats).reduce((acc: any, sectionId: string) => {
      return {
        ...acc,
        [sectionId]: new AssigneeSectionStat(sectionStats[sectionId])
      };
    }, {})
  );
}

function assembleUserQuestionStats(
  questionStats?: UserQuestionStatsReceived
): Map<string, AssigneeQuestionStat> {
  if (!questionStats) {
    return Map() as Map<string, AssigneeQuestionStat>;
  }

  return Map(
    Object.keys(questionStats).reduce((acc: any, questionId: string) => {
      return {
        ...acc,
        [questionId]: new AssigneeQuestionStat({
          ...questionStats[questionId]
        })
      };
    }, {})
  );
}
